"use client";
import { sizes } from "@/components/bloks/Asset";
import { Carousel } from "@/components/ui/Carousel";
import { ProductCardWithCampaigns } from "@/features/campaigns";
import { useTranslations } from "@/i18n/client";
import type { ProductCard as IProductCard, ProductDetails } from "@/lib/centra";
import { useRecommendationClick } from "@/lib/depict/useRecommendationClick";
import { useToken } from "@frend-digital/centra/client";
import { useQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { rpc } from "#rpc";

import styles from "./RelatedProducts.module.css";

export type RelatedProduct = NonNullable<NonNullable<ProductDetails>["relatedProducts"]>[number];

export const RelatedProducts = ({
	productIds,
}: {
	productIds: (string | undefined)[];
}) => {
	const token = useToken();
	const { data: products } = useQuery({
		queryKey: ["depict-recommendations", productIds],
		queryFn: async () => {
			const products = await rpc.depict.recommendations({
				product_ids: productIds,
				type: "related",
				limit: 8,
				token: token!,
			});

			return products;
		},
		enabled: !!productIds.length && !!token,
	});

	const handleProductClick = useRecommendationClick(products, "related_products");

	if (!products || products.length === 0) return null;

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: keyboard navigation handled by individual product cards
		<div className={styles.relatedProducts} onClick={handleProductClick}>
			<Suspense fallback>
				<UnsuspendedRelatedProducts products={products} />
			</Suspense>
		</div>
	);
};

const UnsuspendedRelatedProducts = ({
	products,
}: {
	products: (IProductCard | undefined)[];
}) => {
	const t = useTranslations();

	const isActiveScroll = products?.length > 4;
	return (
		<Carousel
			activeScroll={isActiveScroll}
			title={t("What do you think of these?")}
			showControls={false}
		>
			{products?.map((product) => {
				return (
					<ProductCardWithCampaigns
						sizes={sizes(75, 25)}
						key={product?.displayItemId}
						product={product!}
						showRatings
					/>
				);
			})}
		</Carousel>
	);
};
